import { SCOPE } from '@store/noc/threshold';
import { createSelector } from 'reselect';
import { get } from 'lodash';
import { keys, modes, adKeys, AI_ALERT } from '@components/noc-metric-panel/utils/constants';
import {
  DATASET,
  POPULATION_THRESHOLD_BASELINE,
  INTERNAL_UNKNOWN,
  CURRENT_PRODUCT_MODE,
  isAppModule,
} from '@utils/constants';
import { selector as commonSelector } from '@store/common';
import { getMetricMetaByKey, getMetricUnit, NOC_METRICS } from '@utils/metrics';
import {
  nocTechErrorMetricsFeatureSelector,
  currentMetricsMetaSelector,
} from '@store/common/selectors';
import { combineWithMetricDeDuplication } from '@utils/metrics/metricHelpers';
import featureNames from '@utils/featureToggle/constants';
import {
  getThresholdMetrics,
  getThresholdTechErrorMetrics,
  getThresholdErrorMetrics,
} from '@components/noc-metric-panel/utils/thresholdColumns';
import {
  nocModeSelector,
  trendsViewSelector,
  unsavedThresholdSelector,
  allThresholdCellSelectionSelector,
} from '../base/selectors';
import {
  dataSelector,
  trendsTableDataSortSelector,
  dimensionSelector,
  metricSelector as trendsMetricSelector,
  trendsThresholdPayloadSelector,
} from '../trendsData/selectors';
import {
  filtersTableDataSortSelector,
  metricSelector as filterMetricSelector,
} from '../filtersData/selectors';

const THRESHOLD_MODES = {
  filter: 'filter',
  dimension: 'dimension',
};

const selector = (state) => get(state, SCOPE);
export default selector;

const trendsBody = {
  queries: [
    {
      dataset: DATASET,
      hasAIAlerts: true,
      metrics: [],
      metricsUnit: {},
      filter: [],
      groupBy: [],
      dimension_attributes: [],
      type: THRESHOLD_MODES.dimension,
    },
  ],
};

const filtersBody = {
  queries: [
    {
      dataset: DATASET,
      hasAIAlerts: true,
      metrics: [],
      metricsUnit: {},
      filter: [],
      type: THRESHOLD_MODES.filter,
    },
  ],
};

export const initThresholdSelector: any = createSelector(
  selector,
  nocModeSelector,
  (thresholdStore, mode) => {
    const dataPath = mode === modes.TRENDS ? 'trendsData.response[0]' : 'filtersData.response[0]';

    return {
      data: get(thresholdStore, dataPath, {}),
      isFetching: thresholdStore.isFetching,
    };
  },
);

export const dimensionNameValueMapSelector = createSelector(dataSelector, (dimensionStore) => {
  const results = get(dimensionStore, 'data[0].results', []);
  const mapObj = {};
  results.forEach((result) => {
    const name = result.name.join('');
    const value = result.value.join('');
    mapObj[value] = name;
  });
  return mapObj;
});

export const appBaseLineDataSelector = createSelector(
  initThresholdSelector,
  currentMetricsMetaSelector,
  (thresholdStore, supportedMetrics) => {
    const baseLine = get(thresholdStore, 'data.threshold_baseline', {});
    const baseLineData = {
      id: '1',
      BaseLine: POPULATION_THRESHOLD_BASELINE,
    };
    Object.keys(baseLine).forEach((metricName) => {
      const metric = supportedMetrics[metricName];
      if (metric) {
        baseLineData[metricName] = {
          metric: { ...metric, key: metricName },
          data: {
            custom: baseLine[metricName].custom || '',
            conviva: baseLine[metricName].conviva || '',
            isCustomSelected: baseLine[metricName].isCustomSelected || false,
          },
        };
      }
    });
    return [baseLineData];
  },
);

export const aiBaseLineDataSelector = createSelector(initThresholdSelector, (thresholdStore) => {
  const {
    RebufferingRatio,
    Bitrate,
    VideoStartTime,
    AdSlateDurationPct,
    AdSlatePlaysPct,
    VideoStartFailuresPercentage,
  } = get(thresholdStore, 'data.threshold_baseline', {});

  const getData = (dataObj) =>
    dataObj
      ? {
          custom: dataObj.custom || '',
          conviva: dataObj.conviva || '',
          isCustomSelected: dataObj.isCustomSelected || false,
        }
      : dataObj;

  return [
    {
      id: '1',
      BaseLine: POPULATION_THRESHOLD_BASELINE,
      adCombinedMetricsBundleOne: {
        metric: adKeys.adCombinedMetricsBundleOne,
        data: getData(RebufferingRatio) || getData(Bitrate) || {},
      },
      adCombinedMetricsBundleTwo: {
        metric: adKeys.adCombinedMetricsBundleTwo,
        data: getData(VideoStartFailuresPercentage) || getData(VideoStartTime) || {},
      },
      adCombinedMetricsBundleThree: {
        metric: adKeys.adCombinedMetricsBundleThree,
        data: getData(AdSlateDurationPct) || getData(AdSlatePlaysPct) || {},
      },
    },
  ];
});

export const eibaseLineDataSelector = createSelector(
  initThresholdSelector,
  nocTechErrorMetricsFeatureSelector,
  (thresholdStore, nocTechMetric) => {
    const {
      RebufferingRatio,
      VideoPlaybackFailuresPercentage,
      ExitBeforeVideoStartsPercentage,
      VideoStartFailuresPercentage,
      VideoStartFailuresTechPercentage,
      VideoPlaybackFailuresTechPercentage,
      VideoStartTime,
    } = get(thresholdStore, 'data.threshold_baseline', {});

    const getData = (dataObj) =>
      dataObj
        ? {
            custom: dataObj.custom || '',
            conviva: dataObj.conviva || '',
            isCustomSelected: dataObj.isCustomSelected || false,
          }
        : dataObj;
    const baseLineData = [
      {
        id: '1',
        BaseLine: POPULATION_THRESHOLD_BASELINE,
        eicombinedMetrics: {
          metric: keys.eicombinedMetrics,
          data:
            getData(ExitBeforeVideoStartsPercentage) ||
            getData(VideoStartFailuresPercentage) ||
            getData(VideoStartTime) ||
            getData(VideoStartFailuresTechPercentage) ||
            {},
        },
        RebufferingRatio: { metric: keys.RebufferingRatio, data: getData(RebufferingRatio) || {} },
      },
    ];
    if (nocTechMetric) {
      baseLineData[0].VideoPlaybackFailuresTechPercentage = {
        metric: keys.VideoPlaybackFailuresTech,
        data: getData(VideoPlaybackFailuresTechPercentage) || {},
      };
    } else {
      baseLineData[0].VideoPlaybackFailuresPercentage = {
        metric: keys.VideoPlaybackFailures,
        data: getData(VideoPlaybackFailuresPercentage) || {},
      };
    }
    return baseLineData;
  },
);

export const baseLineDataSelector = createSelector(
  aiBaseLineDataSelector,
  eibaseLineDataSelector,
  appBaseLineDataSelector,
  (aiBaseLineDataStore, eiBaseLineDataStore, appBaseLineDataStore) => {
    switch (CURRENT_PRODUCT_MODE) {
      case 'AI':
        return aiBaseLineDataStore;
      case 'EI':
        return eiBaseLineDataStore;
      case 'APP':
        return appBaseLineDataStore;
      default:
        return eiBaseLineDataStore;
    }
  },
);

export const appContentTableDataSelector = createSelector(
  initThresholdSelector,
  nocModeSelector,
  dimensionNameValueMapSelector,
  currentMetricsMetaSelector,
  (thresholdStore, mode, dimensionStore, supportMetrics) => {
    const data = get(thresholdStore, 'data.thresholds', []);
    const mapNames = (value) => dimensionStore[value] || value;

    return data.map((thresholdDataItem, idx) => {
      const content = {
        id: idx.toString(),
        RowKeys:
          mode === modes.TRENDS
            ? mapNames(thresholdDataItem.dimension_attribute)
            : thresholdDataItem.filter.name,
      };
      Object.keys(thresholdDataItem)
        .filter((key) => !['filter', 'dimension_attribute'].includes(key))
        .forEach((metricName) => {
          const metric = supportMetrics[metricName];
          if (metric) {
            content[metricName] = {
              metric: { ...metric, key: metricName },
              data: {
                custom: thresholdDataItem[metricName].custom || '',
                conviva: thresholdDataItem[metricName].conviva || '',
                isCustomSelected: thresholdDataItem[metricName].isCustomSelected || false,
              },
              identifier:
                mode === modes.TRENDS
                  ? thresholdDataItem.dimension_attribute
                  : thresholdDataItem.filter.name,
              unit: metric.unit,
            };
          }
        });

      return content;
    });
  },
);

export const aiContentTableDataSelector = createSelector(
  initThresholdSelector,
  nocModeSelector,
  dimensionNameValueMapSelector,
  (thresholdStore, mode, dimensionStore) => {
    const data = get(thresholdStore, 'data.thresholds', []);
    const mapNames = (value) => dimensionStore[value] || value;
    const getIdentifier = (thresholdDataItem) =>
      mode === modes.TRENDS ? thresholdDataItem.dimension_attribute : thresholdDataItem.filter.name;
    const getData = (dataObj) =>
      dataObj
        ? {
            custom: dataObj.custom || '',
            conviva: dataObj.conviva || '',
            isCustomSelected: dataObj.isCustomSelected || false,
          }
        : dataObj;
    return data.map((thresholdDataItem, idx) => ({
      id: idx.toString(),
      RowKeys:
        mode === modes.TRENDS
          ? mapNames(thresholdDataItem.dimension_attribute)
          : thresholdDataItem.filter.name,
      VideoStartTime: {
        metric: adKeys.VideoStartTime,
        data: getData(thresholdDataItem.VideoStartTime) || {},
        identifier: getIdentifier(thresholdDataItem),
        unit: getMetricMetaByKey({ metricKey: adKeys.VideoStartTime.key }).unit,
      },
      VideoStartFailuresPercentage: {
        metric: adKeys.VideoStartFailuresPercentage,
        data: getData(thresholdDataItem.VideoStartFailuresPercentage) || {},
        identifier: getIdentifier(thresholdDataItem),
        unit: getMetricMetaByKey({ metricKey: adKeys.VideoStartFailuresPercentage.key }).unit,
      },
      RebufferingRatio: {
        metric: adKeys.RebufferingRatio,
        data: getData(thresholdDataItem.RebufferingRatio) || {},
        identifier: getIdentifier(thresholdDataItem),
        unit: getMetricMetaByKey({ metricKey: adKeys.RebufferingRatio.key }).unit,
      },
      Bitrate: {
        metric: adKeys.Bitrate,
        data: getData(thresholdDataItem.Bitrate) || {},
        identifier: getIdentifier(thresholdDataItem),
        unit: getMetricMetaByKey({ metricKey: adKeys.Bitrate.key }).unit,
      },
      AdSlateDurationPct: {
        metric: adKeys.AdSlateDurationPct,
        data: getData(thresholdDataItem.AdSlateDurationPct) || {},
        identifier: getIdentifier(thresholdDataItem),
        unit: getMetricMetaByKey({ metricKey: adKeys.AdSlateDurationPct.key }).unit,
      },
      AdSlatePlaysPct: {
        metric: adKeys.AdSlatePlaysPct,
        data: getData(thresholdDataItem.AdSlatePlaysPct) || {},
        identifier: getIdentifier(thresholdDataItem),
        unit: getMetricMetaByKey({ metricKey: adKeys.AdSlatePlaysPct.key }).unit,
      },
    }));
  },
);

export const eiContentTableDataSelector = createSelector(
  initThresholdSelector,
  nocModeSelector,
  dimensionNameValueMapSelector,
  nocTechErrorMetricsFeatureSelector,
  (thresholdStore, mode, dimensionStore, nocTechErroeMetric) => {
    const data = get(thresholdStore, 'data.thresholds', []);
    const getIdentifier = (thresholdDataItem) =>
      mode === modes.TRENDS ? thresholdDataItem.dimension_attribute : thresholdDataItem.filter.name;
    const mapNames = (value) => dimensionStore[value] || value; // api returns dimension values, map them to names here
    const getData = (dataObj) =>
      dataObj
        ? {
            custom: dataObj.custom || '',
            conviva: dataObj.conviva || '',
            isCustomSelected: dataObj.isCustomSelected || false,
          }
        : dataObj;

    const dataTable = data.map((thresholdDataItem, idx) => {
      const rowObject: any = {
        id: idx.toString(),
        RowKeys:
          mode === modes.TRENDS
            ? mapNames(thresholdDataItem.dimension_attribute)
            : thresholdDataItem.filter.name,
        ConcurrentPlays: {
          metric: keys.ConcurrentPlays,
          data: getData(thresholdDataItem.ConcurrentPlays) || {},
          identifier: getIdentifier(thresholdDataItem),
          unit: getMetricMetaByKey({ metricKey: keys.ConcurrentPlays.key }).unit,
        },
        ExitBeforeVideoStartsPercentage: {
          metric: keys.ExitBeforeVideoStarts,
          data: getData(thresholdDataItem.ExitBeforeVideoStartsPercentage) || {},
          identifier: getIdentifier(thresholdDataItem),
          unit: getMetricMetaByKey({ metricKey: keys.ExitBeforeVideoStarts.key }).unit,
        },
        RebufferingRatio: {
          metric: keys.RebufferingRatio,
          data: getData(thresholdDataItem.RebufferingRatio) || {},
          identifier: getIdentifier(thresholdDataItem),
          unit: getMetricMetaByKey({ metricKey: keys.RebufferingRatio.key }).unit,
        },
        VideoStartTime: {
          metric: keys.VideoStartTime,
          data: getData(thresholdDataItem.VideoStartTime) || {},
          identifier: getIdentifier(thresholdDataItem),
          unit: getMetricMetaByKey({ metricKey: keys.VideoStartTime.key }).unit,
        },
        ConnectionInducedRebufferingRatio: {
          metric: keys.ConnectionInducedRebufferingRatio,
          data: getData(thresholdDataItem.ConnectionInducedRebufferingRatio) || {},
          identifier: getIdentifier(thresholdDataItem),
          unit: getMetricMetaByKey({ metricKey: keys.ConnectionInducedRebufferingRatio.key }).unit,
        },
        Framerate: {
          metric: keys.Framerate,
          data: getData(thresholdDataItem.Framerate) || {},
          identifier: getIdentifier(thresholdDataItem),
          unit: getMetricMetaByKey({ metricKey: keys.Framerate.key }).unit,
        },
        Bitrate: {
          metric: keys.Bitrate,
          data: getData(thresholdDataItem.Bitrate) || {},
          identifier: getIdentifier(thresholdDataItem),
          unit: getMetricMetaByKey({ metricKey: keys.Bitrate.key }).unit,
        },
      };
      if (nocTechErroeMetric) {
        rowObject.VideoPlaybackFailuresTechPercentage = {
          metric: keys.VideoPlaybackFailuresTech,
          data: getData(thresholdDataItem.VideoPlaybackFailuresTechPercentage) || {},
          identifier: getIdentifier(thresholdDataItem),
          unit: getMetricMetaByKey({ metricKey: keys.VideoPlaybackFailuresTech.key }).unit,
        };
        rowObject.VideoStartFailuresTechPercentage = {
          metric: keys.VideoStartFailuresTech,
          data: getData(thresholdDataItem.VideoStartFailuresTechPercentage) || {},
          identifier: getIdentifier(thresholdDataItem),
          unit: getMetricMetaByKey({ metricKey: keys.VideoStartFailuresTech.key }).unit,
        };
      } else {
        rowObject.VideoPlaybackFailuresPercentage = {
          metric: keys.VideoPlaybackFailures,
          data: getData(thresholdDataItem.VideoPlaybackFailuresPercentage) || {},
          identifier: getIdentifier(thresholdDataItem),
          unit: getMetricMetaByKey({ metricKey: keys.VideoPlaybackFailures.key }).unit,
        };
        rowObject.VideoStartFailuresPercentage = {
          metric: keys.VideoStartFailures,
          data: getData(thresholdDataItem.VideoStartFailuresPercentage) || {},
          identifier: getIdentifier(thresholdDataItem),
          unit: getMetricMetaByKey({ metricKey: keys.VideoStartFailures.key }).unit,
        };
      }
      return rowObject;
    });

    return dataTable;
  },
);

export const contentTableDataSelector = createSelector(
  aiContentTableDataSelector,
  eiContentTableDataSelector,
  appContentTableDataSelector,
  (aiContentTableDataStore, eiContentTableDataStore, appContentTableDataStore) => {
    switch (CURRENT_PRODUCT_MODE) {
      case 'AI':
        return aiContentTableDataStore;
      case 'EI':
        return eiContentTableDataStore;
      case 'APP':
        return appContentTableDataStore;
      default:
        return eiContentTableDataStore;
    }
  },
);

export const thresholdDimensionSelector = createSelector(
  trendsTableDataSortSelector,
  dimensionSelector,
  (trendsDataStore, trendsDimensionStore) => {
    const { data } = trendsDataStore;
    const dimennsionAttributes = data.map((resultItem) => resultItem.value.join(''));
    return {
      dimension: trendsDimensionStore,
      dimension_attributes: dimennsionAttributes,
    };
  },
);

export const thresholdFilterSelector = createSelector(
  filtersTableDataSortSelector,
  (filterDataStore) => {
    const results = filterDataStore.data;
    return results.map((resultItem) => resultItem.filter);
  },
);

export const thresholdFilterPayloadSelector = createSelector(
  filterMetricSelector,
  thresholdFilterSelector,
  commonSelector,
  nocTechErrorMetricsFeatureSelector,
  (filterMetrics, filtersDataSelector, commonStore, nocTechErrorMetric) => {
    let metrics: string[] = getThresholdMetrics();
    const errorMetrics: string[] = nocTechErrorMetric
      ? getThresholdTechErrorMetrics().cols.map((column) => column.key)
      : getThresholdErrorMetrics().cols.map((column) => column.key);
    metrics = combineWithMetricDeDuplication(metrics, errorMetrics);
    if (isAppModule) {
      metrics = combineWithMetricDeDuplication(filterMetrics, NOC_METRICS[CURRENT_PRODUCT_MODE]);
    }
    const metricsUnit = Object.fromEntries(
      metrics.map((metric) => {
        const unit = getMetricUnit({ metricKey: metric });
        return [metric, unit];
      }),
    );
    filtersBody.queries[0].dataset = DATASET;
    filtersBody.queries[0].hasAIAlerts = commonStore.features[featureNames[AI_ALERT]];
    filtersBody.queries[0].metrics = metrics;
    filtersBody.queries[0].metricsUnit = metricsUnit;
    filtersBody.queries[0].filter = filtersDataSelector;
    return filtersBody;
  },
);

export const thresholdTrendsPayloadSelector = createSelector(
  trendsThresholdPayloadSelector,
  trendsMetricSelector,
  thresholdDimensionSelector,
  commonSelector,
  nocTechErrorMetricsFeatureSelector,
  (trendsPayloadStore, trendsMetrics, thresholdDimensionStore, commonStore, nocTechErrorMetric) => {
    let metrics: string[] = getThresholdMetrics();
    const errorMetrics: string[] = nocTechErrorMetric
      ? getThresholdTechErrorMetrics().cols.map((column) => column.key)
      : getThresholdErrorMetrics().cols.map((column) => column.key);
    metrics = combineWithMetricDeDuplication(metrics, errorMetrics);
    if (isAppModule) {
      metrics = combineWithMetricDeDuplication(trendsMetrics, NOC_METRICS[CURRENT_PRODUCT_MODE]);
    }
    const metricsUnit = Object.fromEntries(
      metrics.map((metric) => {
        const unit = getMetricUnit({ metricKey: metric });
        return [metric, unit];
      }),
    );
    const dimension = get(thresholdDimensionStore, 'dimension', '');
    trendsBody.queries[0].dataset = DATASET;
    trendsBody.queries[0].hasAIAlerts = commonStore.features[featureNames[AI_ALERT]];
    trendsBody.queries[0].metrics = metrics;
    trendsBody.queries[0].metricsUnit = metricsUnit;
    trendsBody.queries[0].filter = get(trendsPayloadStore, 'queries[0].filter', {});
    // trendsBody.queries[0].filter = get(commonStore, 'appliedFilters[0]', {});
    trendsBody.queries[0].groupBy = dimension;
    trendsBody.queries[0].dimension_attributes = get(
      thresholdDimensionStore,
      'dimension_attributes',
      [],
    ).filter((dimensionValue) => dimensionValue !== INTERNAL_UNKNOWN);
    return trendsBody;
  },
);

export const thresholdPayloadSelector = createSelector(
  nocModeSelector,
  thresholdFilterPayloadSelector,
  thresholdTrendsPayloadSelector,
  (mode, thresholdFilterStore, thresholdTrendsStore) =>
    mode === modes.FILTERS ? thresholdFilterStore : thresholdTrendsStore,
);

export const appThresholdContentColumnSelector = createSelector(
  nocModeSelector,
  trendsMetricSelector,
  filterMetricSelector,
  currentMetricsMetaSelector,
  (mode, trendsMetrics, filterMetrics, supportedMetrics) => {
    const metrics = mode === modes.TRENDS ? trendsMetrics : filterMetrics;
    const columns: Array<{ key: string; title: string; hideConvivaInput: boolean }> = [];
    metrics.forEach((key) => {
      const metric = supportedMetrics[key];
      if (metric) {
        columns.push({
          key,
          title: metric.displayName,
          hideConvivaInput: false,
        });
      }
    });
    return columns;
  },
);

export const trendsSavePayloadSelector = createSelector(
  trendsViewSelector,
  thresholdDimensionSelector,
  (trendsStore, thresholdDimensionStore) => {
    const appliedFilter = get(trendsStore, 'appliedFilter[0]', {});
    const dimension = get(thresholdDimensionStore, 'dimension', '');
    return {
      queries: [
        {
          dataset: DATASET,
          filter: appliedFilter,
          dimension,
          type: THRESHOLD_MODES.dimension,
        },
      ],
    };
  },
);

export const filtersSavePayloadSelector = createSelector(nocModeSelector, (mode) => ({
  queries: [
    {
      dataset: DATASET,
      type: THRESHOLD_MODES.filter,
    },
  ],
}));

export const loadingSelector = createSelector(
  selector,
  nocModeSelector,
  (commonThresholdSelector, mode) => {
    const isFetching =
      mode === modes.TRENDS
        ? commonThresholdSelector.trendsData.isFetching
        : commonThresholdSelector.filtersData.isFetching;

    const errorCode =
      mode === modes.TRENDS
        ? commonThresholdSelector.trendsData.errorCode
        : commonThresholdSelector.filtersData.errorCode;

    const errorMsg =
      mode === modes.TRENDS
        ? commonThresholdSelector.trendsData.errorMsg
        : commonThresholdSelector.filtersData.errorMsg;

    return {
      loading: isFetching,
      errorCode,
      errorMsg,
    };
  },
);

export const thresholdCellConfigSelector = createSelector(
  unsavedThresholdSelector,
  allThresholdCellSelectionSelector,
  commonSelector,
  (unsavedThresholdChanges, allThresholdCellSelection, commonStore) => ({
    unsavedThresholdChanges,
    allThresholdCellSelection,
    aiThresholdEnabled:
      commonStore.features[featureNames[AI_ALERT]] &&
      commonStore.features[featureNames.REALTIME_AI_THRESHOLD],
    thresholdDisabled: commonStore.features[featureNames.DISABLE_THRESHOLD],
  }),
);

// threshold feature flag selector
export const isThresholdDisabledSelector = createSelector(commonSelector, (commonStore) =>
  get(commonStore, ['features', featureNames.DISABLE_THRESHOLD], null),
);
